import type { AttachmentChip } from '@/services/assistant/attachments/attachment-chip'
import { AlertCircle, Database, FileText, Loader2, X } from 'lucide-react'

export interface AttachmentChipsProps {
  chips: readonly AttachmentChip[]
  onRemove: (id: string) => void
}

function statusText(chip: AttachmentChip): string | null {
  switch (chip.status) {
    case 'preparing':
      return 'Preparing…'
    case 'queued':
      return 'Waiting to upload…'
    case 'uploading':
      return `Uploading ${chip.progress ?? 0}%`
    case 'ready':
      return chip.kind === 'data' ? 'In your Library' : null
    default:
      return chip.reason ?? null
  }
}

function ChipIcon({ chip }: { chip: AttachmentChip }) {
  if (chip.status === 'refused' || chip.status === 'failed') {
    return <AlertCircle className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
  }

  if (chip.status === 'preparing' || chip.status === 'uploading' || chip.status === 'queued') {
    return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" aria-hidden="true" />
  }

  if (chip.previewUrl) return <img src={chip.previewUrl} alt="" className="h-6 w-6 shrink-0 rounded object-cover" />
  if (chip.kind === 'data') return <Database className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />

  return <FileText className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
}

/**
 * The files attached to the message being written, above the composer's input: each with what is
 * happening to it, upload progress, or why it cannot go, and a ✕ to take it off.
 */
export function AttachmentChips({ chips, onRemove }: AttachmentChipsProps) {
  if (chips.length === 0) return null

  return (
    <ul className="flex flex-wrap gap-[0.35em]" aria-label="Attachments">
      {chips.map(chip => {
        const bad = chip.status === 'refused' || chip.status === 'failed'
        const status = statusText(chip)

        return (
          <li
            key={chip.id}
            data-testid="attachment-chip"
            data-status={chip.status}
            className={`relative flex max-w-[14em] items-center gap-[0.35em] overflow-hidden rounded-md border px-[0.4em] py-[0.2em] text-[0.72em] ${
              bad
                ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300'
                : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
            }`}
          >
            <ChipIcon chip={chip} />
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate" title={chip.name}>
                {chip.name}
              </span>
              {status && (
                <span className={`truncate ${bad ? '' : 'text-gray-500 dark:text-gray-400'}`} title={status}>
                  {status}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => onRemove(chip.id)}
              aria-label={`Remove ${chip.name}`}
              title={`Remove ${chip.name}`}
              className="shrink-0 rounded p-[0.1em] hover:bg-black/5 dark:hover:bg-white/10"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            {chip.status === 'uploading' && (
              <span
                role="progressbar"
                aria-label={`Uploading ${chip.name}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={chip.progress ?? 0}
                className="absolute bottom-0 left-0 h-0.5 bg-primary-500"
                style={{ width: `${chip.progress ?? 0}%` }}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}
